/* eslint-disable @typescript-eslint/no-unused-vars */
import { Box, Drawer, Modal, useMediaQuery } from '@mui/material';
import { FC, useEffect, useReducer, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from 'src/assets/svg/close-icon.svg';
import { useGetIsValidRebooker } from 'src/lib/hooks/use-get-is-valid-rebooker';
import { useUpdateBasket } from 'src/lib/hooks/use-patch-update-basket';
import { Currency } from 'src/shared/enum/Currency';
import { theme } from 'src/styles/theme';
import {
    getAgentCode,
    getAgentStudentId,
    getPromotionId,
} from 'src/utils/local-storage-versioning';
import { useFetchCreatePaymentPlan } from 'src/lib/hooks/use-create-payment-plan';
import { bookingDetailsReducer } from 'src/pages/booking-summary/booking-details-reducer';
import { usePostSubmitBasket } from 'src/pages/booking-summary/hooks/post-submit-basket';
import { Term } from 'src/shared/types/booking-types';
import { PaymentPlanReducerTypes } from 'src/pages/booking-summary/types';
import { InstalmentType } from 'src/shared/enum/InstalmentType';
import { useGetBuildingRebookerActive } from 'src/lib/hooks/use-get-rebooker-active';

import { BookingDetailsView } from '../booking-summary/booking-details';
import { PaymentPlanView } from '../payment-plan/payment-plan-view';
import { Button } from '../../components/button';

import { styles } from './styles';

import type { BookingDetailsDialogProps } from './types';

export const BookingDetailsDialog: FC<BookingDetailsDialogProps> = ({
    isOpen,
    handleIsOpen,
    calculatedBookingDetails,
    userChoices: initialUserChoices,
    basket,
}) => {
    const [searchParams] = useSearchParams();
    const isDrawer = useMediaQuery(theme.breakpoints.down('lg'));
    const { room: roomData, bookingDetails } = calculatedBookingDetails;
    const isOriaRoom = roomData.room_code.includes('OR1');

    // TODO: The promo id is not used. It will be set based on whether the user is a valid rebooker or not. Currently hardcoded to null.
    const [promotionId, setPromotionId] = useState<string | null>(null);
    const paymentPlanId = null;

    const { mutateAsync: updateBasketAsync } = useUpdateBasket();
    const isRebooker = basket.options.rebooker;
    const roomCode = roomData?.room_code ?? '';

    const { data: isBuildingInRebookerPhase, isLoading: isBuildingRebookerLoading } =
        useGetBuildingRebookerActive(roomCode, isRebooker);

    const isRebookerFlowActive =
        isRebooker && !isBuildingRebookerLoading && Boolean(isBuildingInRebookerPhase);

    const { data: isValidRebookerResponse } = useGetIsValidRebooker(roomCode, isRebookerFlowActive);
    const isValidRebooker = isValidRebookerResponse ?? null;

    const agentCode = getAgentCode(searchParams);
    const agentStudentId = getAgentStudentId(searchParams);
    const { t } = useTranslation();

    const [userChoices, dispatchUserChoices] = useReducer(
        bookingDetailsReducer,
        initialUserChoices
    );
    const paymentPlanChoices = {
        ...userChoices,
        term: userChoices.term?.name ?? null,
    };

    const { data: createdPaymentPlan, isFetching: fetchingPaymentPlan } = useFetchCreatePaymentPlan(
        {
            userChoices: paymentPlanChoices,
            promotionId: isValidRebooker?.promotion_id ? isValidRebooker.promotion_id : null,
            isRebooker,
            fillerWeekEligible: false,
            roomData: calculatedBookingDetails?.room ?? null,
            bookingId: null,
        }
    );

    const { mutateAsync: submitBasket } = usePostSubmitBasket();

    const handleTermChange = (value: Term) => {
        dispatchUserChoices({ type: PaymentPlanReducerTypes.SET_TERM, data: value });

        const selectedInstalment =
            value.instalments.find((i) => i.name === userChoices.instalmentType) ??
            value.instalments[0];

        dispatchUserChoices({
            type: PaymentPlanReducerTypes.SET_INSTALMENT_TYPE,
            data: selectedInstalment.name as InstalmentType,
        });

        dispatchUserChoices({
            type: PaymentPlanReducerTypes.SET_INSTALMENT_ID,
            data: selectedInstalment.id,
        });
    };

    const handleInstalmentChange = (value: string | null, id: string | null) => {
        dispatchUserChoices({
            type: PaymentPlanReducerTypes.SET_INSTALMENT_TYPE,
            data: value as InstalmentType,
        });

        dispatchUserChoices({
            type: PaymentPlanReducerTypes.SET_INSTALMENT_ID,
            data: id,
        });
    };

    const incrementStudentNumber = () => {
        if (!calculatedBookingDetails) return;

        const max = calculatedBookingDetails.bookingDetails.options.students.maxStudents;

        if ((userChoices.numberOfStudents ?? 0) < max) {
            dispatchUserChoices({
                type: PaymentPlanReducerTypes.SET_NUMBER_OF_STUDENTS,
                data: (userChoices.numberOfStudents ?? 1) + 1,
            });
        }
    };

    const decrementStudentNumber = () => {
        if (!calculatedBookingDetails) return;

        const min = calculatedBookingDetails.bookingDetails.options.students.minStudents;

        if ((userChoices.numberOfStudents ?? 0) > min) {
            dispatchUserChoices({
                type: PaymentPlanReducerTypes.SET_NUMBER_OF_STUDENTS,
                data: (userChoices.numberOfStudents ?? 1) - 1,
            });
        }
    };

    const setNumberOfStudents = (students: number) =>
        dispatchUserChoices({
            type: PaymentPlanReducerTypes.SET_NUMBER_OF_STUDENTS,
            data: students,
        });

    const handleSubmit = async () => {
        if (!createdPaymentPlan) {
            handleIsOpen();
            return;
        }

        try {
            await submitBasket({
                room: calculatedBookingDetails.room,
                paymentPlanId,
                userChoices,
                paymentPlan: createdPaymentPlan,
                basket,
                isValidRebooker,
                agentCode,
                agentStudentId,
                cbl: basket.products[0].cbl,
                paymentOptions: calculatedBookingDetails.bookingDetails.terms,
            });
        } finally {
            handleIsOpen();
        }
    };

    useEffect(() => {
        if (isValidRebooker?.rebooker) {
            setPromotionId(isValidRebooker.promotion_id);

            updateBasketAsync({
                basketId: basket.id,
                updatedAt: basket.updated_date,
                basketData: {
                    options: {
                        ...basket.options,
                        rebooker: isValidRebooker.rebooker,
                        rebookerPhase: isValidRebooker.rebookers_stage,
                        fillerWeekEligible: isValidRebooker.filler_week_eligible,
                        agentCode,
                        agentStudentId,
                        referrerCode: basket.options.referrerCode,
                    },
                    payment_options_snapshot: calculatedBookingDetails.bookingDetails.terms,
                },
            });
        }
    }, [isValidRebooker]);

    useEffect(() => {
        const pid = getPromotionId(searchParams);

        if (pid) setPromotionId(pid);
    }, []);

    const content = () => {
        const currency = bookingDetails.currency as Currency;

        return (
            <Box css={styles.container}>
                <Box css={styles.header}>
                    <CloseIcon css={styles.closeIcon} onClick={handleIsOpen} />
                </Box>

                <Box css={styles.contentWrapper}>
                    <BookingDetailsView
                        setNumberOfStudents={setNumberOfStudents}
                        handleTermChange={handleTermChange}
                        handleInstalmentChange={handleInstalmentChange}
                        incrementStudentNumber={incrementStudentNumber}
                        decrementStudentNumber={decrementStudentNumber}
                        roomData={calculatedBookingDetails.room}
                        bookingDetails={calculatedBookingDetails.bookingDetails}
                        userChoices={userChoices}
                        loading={false}
                        isBookingDetailsDialog
                    />
                </Box>

                <Box css={styles.bgGray}>
                    <Box css={styles.contentWrapper}>
                        <PaymentPlanView
                            restStyles={styles.payment}
                            showPaymentOptions={false}
                            ctaLabel="edit_booking_details_cta"
                            withHeaderStipe={false}
                            isEditBookingDetailsDialog={isOpen}
                            currency={currency}
                            loading={fetchingPaymentPlan}
                            createdPaymentPlan={createdPaymentPlan}
                            showSantanderCards={isOriaRoom}
                        />

                        <Button
                            variant="contained"
                            css={styles.cta}
                            onClick={handleSubmit}
                            loading={false}
                            disabled={fetchingPaymentPlan}
                            id="submit-button"
                        >
                            {t('edit_booking_details_cta')}
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    };

    if (isDrawer) {
        return (
            <Drawer open={isOpen} anchor="bottom" PaperProps={{ sx: styles.drawer }}>
                {content()}
            </Drawer>
        );
    }

    return (
        <Modal open={isOpen} aria-labelledby="booking-details-modal">
            <Box css={styles.modal}>{content()}</Box>
        </Modal>
    );
};
